import { groundStyles } from "@/constants/maptilerStyleOptions";
import type { LayerCreateUnion } from "@/layers";
import {
  HighchartsYAxis,
  HighchartsYAxisPointForecast,
} from "@/overlay/components/CommonParts/PlotHighchart/utils/chart-options/axis-default";
import { DEFAULT_MAP_PROJECTION } from "@/overlay/components/LayerStackWindow";
import { DefaultPlotSeries, DefaultPointForecastSeriesList, type PlotCreate } from "@/reducer/client-models";
import type { ExtendedEnergyPlotCreate } from "@/types/data";
import { t } from "@lingui/core/macro";
import {
  type BackgroundMapLayerCreate,
  type CartographicMapCreate,
  type CountryPlotCreate,
  type CrossSectionCreate,
  type GridCellLayout,
  LayoutType,
  type LocationTableCreate,
  type NoteCreate,
  SoundingParamType,
  type TabCreate,
  type TephigramCreate,
  VerticalInterpolationType,
  ViewportKind,
  type WeatherTableCreate,
} from "@mm/metx-workbench.meteomatics.com";
import { createDeltaSeriesStyling } from "./colorGenerator";
import { defaultDateTimeDesc } from "./defaultDateTimeDesc";

const getNewTitle = () => {
  return t`New Title`;
};

export function createNewPlot(gridCellLayout: GridCellLayout): PlotCreate {
  return {
    datetime_from_offset: null,
    datetime_to_offset: null,
    legendStyle: null,
    time_lock: false,
    titleStyle: null,
    plotSeries: [DefaultPlotSeries],
    gridCellLayout: gridCellLayout,
    location: { lat: 52.51704, lng: 13.38886 },
    location_info: { coordinates: { lat: 52.51704, lon: 13.38886 } },
    datetime_from: null,
    datetime_to: null,
    resolution: "PT1H",
    plotYAxis: HighchartsYAxis,
    title: getNewTitle(),
  };
}

/**
 * Portfolio plots start without a weather series so the user can configure their
 * portfolio series after creating or selecting a portfolio in the manager.
 */
export function createNewPortfolioPlot(gridCellLayout: GridCellLayout): PlotCreate {
  return {
    ...createNewPlot(gridCellLayout),
    plotSeries: [],
  };
}

export function createNewPointForecastPlot(gridCellLayout: GridCellLayout): PlotCreate {
  return {
    datetime_from_offset: null,
    datetime_to_offset: null,
    legendStyle: null,
    titleStyle: null,
    plotSeries: [...DefaultPointForecastSeriesList],
    gridCellLayout: gridCellLayout,
    location: { lat: 52.51704, lng: 13.38886 },
    datetime_from: null,
    datetime_to: null,
    resolution: "PT1H",
    title: "",
    plotYAxis: HighchartsYAxisPointForecast,
  };
}

const DEFAULT_ENERGY_PLOT_MODEL_COLOR = "#094e98";

export const energyPlotModelColors = {
  mix: "#8979FF",
  "mm-euro1k": "#FF928A",
  "mm-us1k": "#59DD87",
  "cmc-gem": "#F9C043",
  "dwd-icon-eu": "#64A5F6",
  "ecmwf-ens": "#F05555",
  "ecmwf-ifs": "#F77BE6",
  "ncep-gfs": "#2150DC",
  "ncep-hrrr": "#c6c83b",
} as const;

export function getEnergyPlotModelColor(modelId?: string | null): string {
  if (!modelId) {
    return DEFAULT_ENERGY_PLOT_MODEL_COLOR;
  }

  return energyPlotModelColors[modelId as keyof typeof energyPlotModelColors] ?? DEFAULT_ENERGY_PLOT_MODEL_COLOR;
}

export const deltaSeriesStyling = createDeltaSeriesStyling(DEFAULT_ENERGY_PLOT_MODEL_COLOR);

export function createEnergyPlotSeriesStyles(model: string) {
  const powerColor = getEnergyPlotModelColor(model);

  return {
    actual: {
      type: "spline",
      dashStyle: "solid",
      color: "#FF0000",
      fontSize: "12px",
      visible: true,
      showInLegend: true,
    },
    power: {
      type: "spline",
      dashStyle: "solid",
      color: powerColor,
      fontSize: "12px",
      visible: true,
      showInLegend: true,
    },
    ...createDeltaSeriesStyling(powerColor),
  };
}

export function createEnergyPlotGroup(model: string) {
  return {
    model,
    plotSeriesStyles: createEnergyPlotSeriesStyles(model),
  };
}

export function createNewEnergyPlot(
  forecastType: string,
  model: string,
  siteId: string,
  gridCellLayout: GridCellLayout,
): ExtendedEnergyPlotCreate {
  return {
    gridCellLayout: gridCellLayout,
    forecast_type: forecastType,
    unit: "GW",
    site_id: siteId,
    model: model,
    datetime_from_offset: null,
    datetime_to_offset: null,
    datetime_from: null,
    datetime_to: null,
    titleStyle: null,
    legendStyle: null,
    sub_site_ids: null,
    ens_members: null,
    title: getNewTitle(),

    plotSeriesStyles: createEnergyPlotSeriesStyles(model), // find a way to add plotSeriesStyles dynamically
  };
}

export function createNewWeatherTable(
  tabId: number,
  title = "t_2m:C",
  parameter_unit = "t_2m:C",
  model = "mix",
): WeatherTableCreate {
  return {
    resolution: "PT1H",
    time_lock: false,
    title: getNewTitle(),
    titleStyle: null,
    location: { lat: 52.51704, lng: 13.38886 },
    location_info: { coordinates: { lat: 52.51704, lon: 13.38886 } },
    id_tab: tabId,
    datetime_from: null,
    datetime_from_offset: null,
    datetime_to: null,
    datetime_to_offset: null,
    groups: [
      {
        group_info: {
          title: "Default group",
          show: true,
        },
        columns: [
          {
            title: title,
            parameter_unit: parameter_unit,
            model: model,
            show: true,
            index: 0,
            calibrated: true,
            ens_select: "",
            color: "#000000",
          },
        ],
      },
    ],
  };
}

export function createNewLocationTable(
  tabId: number,
  title = getNewTitle(),
  parameter_unit = "t_2m:C",
  model = "mix",
): LocationTableCreate {
  return {
    advanced_thresholds: [],
    datetime_from: null,
    datetime_from_offset: null,
    datetime_to: null,
    datetime_to_offset: null,
    resolution: "PT1H",
    thresholds: [],
    titleStyle: null,
    title: title,
    id_tab: tabId,
    model: model,
    parameter_unit: parameter_unit,
    // Example

    groups: [
      {
        locations: [
          { lat: 52.519, lon: 13.404, label: "Berlin" },
          { lat: 47.42, lon: 9.38, label: "St. Gallen" },
        ],
      },
    ],
  };
}

export const TEPHIGRAM_DEFAULT_LEVEL_UNIT = "hPa";

export const DEFAULT_SOUNDING_TYPE: SoundingParamType = SoundingParamType.ML;
export function createNewTephigram(id_tab: number): TephigramCreate {
  return {
    legendStyle: null,
    id_tab: id_tab,
    title: getNewTitle(),
    location: { lat: 52.51704, lng: 13.38886 },
    location_info: { coordinates: { lat: 52.51704, lon: 13.38886 } },
    model: "mix",
    levelUnit: TEPHIGRAM_DEFAULT_LEVEL_UNIT,
    soundingType: DEFAULT_SOUNDING_TYPE,
    titleStyle: null,
  };
}

export function createNewCrossSection(id_tab: number): CrossSectionCreate {
  return {
    id_tab: id_tab,
    title: getNewTitle(),
    location: { lat: 52.51704, lng: 13.38886 },
    location_info: { coordinates: { lat: 52.51704, lon: 13.38886 } },
    model: "mix",
    datetime_from: null,
    datetime_from_offset: null,
    datetime_to: null,
    datetime_to_offset: null,
    time_lock: false,
    titleStyle: null,
    legendStyle: null,
  };
}

export function createNewCountryPlot(id_tab: number): CountryPlotCreate {
  return {
    id_tab: id_tab,
    model: "mix",
    title: getNewTitle(),
    resolution: "PT1H",
    datetime_from: null,
    datetime_to: null,
    datetime_from_offset: null,
    datetime_to_offset: null,
    time_lock: false,
    titleStyle: null,
    plotSeries: [
      {
        ...DefaultPlotSeries,
        parameter_unit: "t_2m_10y_mean:C",
      },
      {
        ...DefaultPlotSeries,
        series_color: "#FF3333",
        parameter_unit: "t_2m:C",
      },
    ],
    site_id: "de",
    custom_options: { show_live_temperature: true, show_live_10y_temperature: true },
  };
}

export function createNewNote(tabId: number, title = getNewTitle()): NoteCreate {
  return {
    content: null,
    title,
    id_tab: tabId,
  };
}

export const defaultBackgroundMapLayer: BackgroundMapLayerCreate = {
  calibrated: false,
  vertical_interpolation: VerticalInterpolationType.none,
  index: 0,
  opacity: 1,
  show: true,
  style: groundStyles[0].id,
  custom_options: { show_state_border: false },
  kind: "BackgroundMapDescription",
};

export function createNewMap(gridCellLayout: GridCellLayout, layers?: LayerCreateUnion[]): CartographicMapCreate {
  return {
    drawing: null,
    legend_size: null,
    map_projection: DEFAULT_MAP_PROJECTION,
    titleStyle: null,
    title: getNewTitle(),
    gridCellLayout: gridCellLayout,
    layers: layers ?? [],
    time_offset_mins: 0,
    lod_bias: 0,
  };
}

export function newTab(): TabCreate {
  return {
    show_location_markers_on_map: false,
    weather_tables: [],
    order: null,
    title: "New Tab",
    datetime: defaultDateTimeDesc,
    maps: [
      {
        ...createNewMap(
          {
            gridColumnStart: 1,
            gridColumnEnd: 97,
            gridRowStart: 1,
            gridRowEnd: 97,
          },
          [defaultBackgroundMapLayer],
        ),
      },
    ],
    viewports: [
      {
        kind: ViewportKind.ViewportCenterPoint,
        center_lng: 0,
        center_lat: 0,
        zoom: 0,
        southWest_lng: 0,
        southWest_lat: 0,
        northEast_lng: 0,
        northEast_lat: 0,
      },
    ],
    plots: [],
    energy_plots: [],
    tephigrams: [],
    country_plots: [],
    layouts: [
      {
        type: LayoutType.Map,
        id_tool: null,
        gridCellLayout: {
          gridColumnStart: 1,
          gridColumnEnd: 97,
          gridRowStart: 1,
          gridRowEnd: 97,
        },
      },
    ],
  };
}

export const newEmptyTab: TabCreate = {
  show_location_markers_on_map: false,
  title: "New Tab",
  datetime: defaultDateTimeDesc,
  order: null,
  maps: [],
  viewports: [],
  plots: [],
  energy_plots: [],
  tephigrams: [],
  country_plots: [],
  weather_tables: [],
  layouts: [
    {
      type: LayoutType.Undefined,
      id_tool: null,
      gridCellLayout: {
        gridColumnStart: 1,
        gridColumnEnd: 97,
        gridRowStart: 1,
        gridRowEnd: 97,
      },
    },
  ],
};
